/**
 * Email several estimates as one zip (#1349).
 *
 * Built to `threads/bulk-estimate-email/mockup.html` (approved 2026-08-31) —
 * that file is the contract, and a deviation from it is a bug.
 *
 * ── The shape of the flow ────────────────────────────────────────────────
 *
 *   preview  →  edit recipients  →  send  →  result
 *
 * Nothing is sent until the footer button is pressed, and the preview shows
 * what WILL happen and what WILL NOT, in that order, with the real filenames.
 * The server renders every PDF for the preview, so an estimate that cannot be
 * turned into a PDF surfaces HERE — while "send the other four" is still a
 * choice — instead of going missing from a zip nobody inspected.
 *
 * ── Why the filenames are on screen at all ───────────────────────────────
 *
 * They are the whole point of the feature: the customer sorts a folder by
 * them and never opens the PDFs. So the office sees the exact names before
 * the email leaves, and the names shown are the names sent — both come from
 * the same server call.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Download, Info, Loader2, Mail, X } from "lucide-react";
import { api, downloadAuthedFile } from "@/lib/api";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatDollars } from "@/lib/utils";
import { useTableControls } from "@/lib/use-table-controls";
import { SortHeaderButton } from "@/components/ui/sort-header-button";
import { draftWarning, fileSize, sendButtonLabel, sendResultLine, validateSelection } from "./estimate-batch-copy";

interface PreviewEntry {
    estimateId: string;
    displayNumber: string | null;
    title: string | null;
    siteName: string | null;
    siteCity: string | null;
    woNumber: string | null;
    status: string;
    subtotal: number;
    taxAmount: number;
    filename: string;
    bytes: number;
}

const ENTRY_SORT: Record<string, (e: PreviewEntry) => string | number | null> = {
    estimate: (e) => e.displayNumber ?? e.estimateId,
    location: (e) => [e.siteName, e.siteCity].filter(Boolean).join(" · ") || null,
    wo: (e) => e.woNumber,
    status: (e) => e.status,
    subtotal: (e) => e.subtotal,
};

interface Exclusion {
    estimateId: string;
    displayNumber: string | null;
    siteName: string | null;
    reason: string;
}

interface PreviewRecipient {
    value: string;
    contactMethodId: string | null;
    contactName: string | null;
    reason: string;
}

interface Preview {
    ok: boolean;
    code?: string;
    message?: string;
    customerName: string | null;
    archiveName: string;
    entries: PreviewEntry[];
    failed: Exclusion[];
    skipped: Exclusion[];
    recipients: PreviewRecipient[];
    totals: { count: number; subtotal: number; taxAmount: number; total: number; zipBytes: number };
    draftIds: string[];
    resendIds: string[];
    emailConfigured: boolean;
}

interface SendResult {
    ok: boolean;
    sentTo: string[];
    failedTo: string[];
    entries: PreviewEntry[];
    markedSent: string[];
    archiveName: string | null;
}

interface ContactMethod {
    id: string;
    kind: string;
    value: string;
}
interface ContactWithMethods {
    id: string;
    firstName: string | null;
    lastName: string | null;
    methods: ContactMethod[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A recipient chip: the address, why it was picked, and one click to drop it. */
function RecipientChip({ r, onRemove }: { r: PreviewRecipient; onRemove: () => void }) {
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted py-0.5 pl-2.5 pr-1 text-xs">
            {r.contactName ? (
                <span>
                    <span className="font-medium">{r.contactName}</span> · {r.value}
                </span>
            ) : (
                <span>{r.value}</span>
            )}
            {r.reason && (
                <span className="rounded-full border bg-card px-1.5 text-[9.5px] font-semibold uppercase tracking-wide text-primary">
                    {r.reason}
                </span>
            )}
            <button
                type="button"
                aria-label={`Remove ${r.value}`}
                className="px-1 text-muted-foreground hover:text-foreground"
                onClick={onRemove}
            >
                ×
            </button>
        </span>
    );
}

export function EstimateSendDialog({
    customerId,
    customerName,
    estimateIds,
    open,
    onClose,
    onSent,
}: {
    customerId: string;
    customerName: string | null;
    /** The office's selection, in the order it was picked. */
    estimateIds: string[];
    open: boolean;
    onClose: () => void;
    /** Fired after a send that reached at least one address. */
    onSent: () => void;
}) {
    const qc = useQueryClient();
    const [recipients, setRecipients] = useState<PreviewRecipient[]>([]);
    const [newEmail, setNewEmail] = useState("");
    const [seeded, setSeeded] = useState(false);
    const [result, setResult] = useState<SendResult | null>(null);

    const preview = useQuery({
        queryKey: ["estimate-bulk-preview", customerId, estimateIds],
        queryFn: () => api.post<Preview>("/api/estimates/bulk-send/preview", { customerId, estimateIds }),
        enabled: open && estimateIds.length > 0,
        // Every open re-renders the PDFs, which is the point: an estimate
        // edited since the last look must not send from a stale preview.
        staleTime: 0,
        retry: false,
    });

    const contacts = useQuery({
        queryKey: ["customer-contact-methods", customerId],
        queryFn: () => api.get<{ contacts: ContactWithMethods[] }>(`/api/customers/${customerId}/contact-methods`),
        enabled: open,
    });

    // Seed the chips from the server's routing ONCE per opening, so the
    // office's edits survive a refetch instead of being silently reverted.
    useEffect(() => {
        if (!open) {
            setSeeded(false);
            setResult(null);
            setNewEmail("");
            return;
        }
        if (seeded || !preview.data?.ok) return;
        setRecipients(preview.data.recipients);
        setSeeded(true);
    }, [open, seeded, preview.data]);

    const data = preview.data;
    const entries = data?.entries ?? [];
    const selectionError = validateSelection(estimateIds.length);

    const draftNumbers = useMemo(
        () => entries.filter((e) => e.status === "draft").map((e) => `#${e.displayNumber ?? e.estimateId.slice(0, 8)}`),
        [entries],
    );
    const {
        visible: sortedEntries,
        sortKey,
        sortDir,
        toggleSort,
    } = useTableControls(entries, {
        sortAccessors: ENTRY_SORT,
        sortId: "customer.estimate-send",
        descFirst: ["subtotal"],
    });
    const sortTh = (key: string, label: string) => (
        <SortHeaderButton
            label={label}
            active={sortKey === key}
            dir={sortDir}
            onClick={() => toggleSort(key)}
            className="uppercase tracking-wide"
        />
    );
    const warning = draftWarning(draftNumbers, entries.length - draftNumbers.length);

    const alreadyPicked = new Set(recipients.map((r) => r.value.toLowerCase()));
    const addable = (contacts.data?.contacts ?? [])
        .flatMap((c) =>
            c.methods
                .filter((m) => m.kind === "email")
                .map((m) => ({
                    value: m.value,
                    contactMethodId: m.id,
                    contactName: [c.firstName, c.lastName].filter(Boolean).join(" ") || null,
                    reason: "",
                })),
        )
        .filter((r) => !alreadyPicked.has(r.value.toLowerCase()));

    const addRecipient = (value: string, meta?: Partial<PreviewRecipient>) => {
        const v = value.trim();
        if (!EMAIL_RE.test(v)) {
            toast.error(`"${v}" doesn't look like an email address.`);
            return;
        }
        setRecipients((prev) =>
            prev.some((r) => r.value.toLowerCase() === v.toLowerCase())
                ? prev
                : [...prev, { value: v, contactMethodId: null, contactName: null, reason: "Added", ...meta }],
        );
        setNewEmail("");
    };

    const send = useMutation({
        mutationFn: () =>
            api.post<SendResult>("/api/estimates/bulk-send", {
                customerId,
                estimateIds,
                recipients: recipients.map((r) => r.value),
            }),
        onSuccess: (res) => {
            setResult(res);
            qc.invalidateQueries({ queryKey: ["customer", customerId, "estimates"] });
            qc.invalidateQueries({ queryKey: ["site-commercial-docs"] });
            if (res.sentTo.length > 0) onSent();
        },
        onError: (e: unknown) => {
            const err = e as { body?: { message?: string } };
            toast.error(err?.body?.message ?? "Couldn't email these estimates.");
        },
    });

    const download = async () => {
        try {
            await downloadAuthedFile(
                `/api/estimates/bulk.zip?customerId=${customerId}&ids=${estimateIds.join(",")}`,
                data?.archiveName ?? "estimates.zip",
            );
        } catch {
            toast.error("Couldn't build the zip.");
        }
    };

    const title = `Email ${estimateIds.length} estimate${estimateIds.length === 1 ? "" : "s"}${
        customerName ? ` — ${customerName}` : ""
    }`;

    return (
        <Dialog open={open} onClose={onClose} title={title} className="max-w-4xl">
            {selectionError ? (
                <p
                    className="py-10 text-center text-sm text-muted-foreground"
                    data-testid="estimate-bulk-selection-error"
                >
                    {selectionError}
                </p>
            ) : preview.isLoading ? (
                <div className="flex flex-col items-center gap-2 py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">Building the PDFs…</p>
                </div>
            ) : !data || !data.ok ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                    {data?.message ?? "Couldn't work out what to send. Close this and try again."}
                </p>
            ) : result ? (
                /* ── Result: counts and addresses, never a bare tick ── */
                <div className="space-y-3 p-5" data-testid="estimate-bulk-result">
                    <div
                        className={
                            result.failedTo.length === 0
                                ? "flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
                                : "flex items-start gap-2 rounded-md border border-red-400/60 bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200"
                        }
                    >
                        {result.failedTo.length === 0 ? (
                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
                        ) : (
                            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                        )}
                        <span>{sendResultLine(result.sentTo, result.failedTo, result.entries.length)}</span>
                    </div>
                    {result.markedSent.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                            {result.markedSent.length} draft{result.markedSent.length === 1 ? " is" : "s are"} now
                            marked Sent. Logged on each estimate&apos;s timeline and on the customer&apos;s send
                            history.
                        </p>
                    )}
                    <div className="flex justify-end">
                        <Button size="sm" onClick={onClose}>
                            Done
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3 p-5">
                    {/* ── What's going out ── */}
                    <div>
                        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Going out
                        </div>
                        <div className="overflow-hidden rounded-md border">
                            <table className="w-full text-xs">
                                <thead className="bg-muted/70">
                                    <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                                        <th className="px-2 py-1.5">{sortTh("estimate", "Estimate")}</th>
                                        <th className="px-2 py-1.5">{sortTh("location", "Location")}</th>
                                        <th className="px-2 py-1.5">{sortTh("wo", "WO #")}</th>
                                        <th className="px-2 py-1.5">{sortTh("status", "Status")}</th>
                                        <th className="px-2 py-1.5 text-right">{sortTh("subtotal", "Subtotal")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedEntries.map((e) => (
                                        <tr key={e.estimateId} className="border-t">
                                            <td className="px-2 py-1.5 font-mono">
                                                #{e.displayNumber ?? e.estimateId.slice(0, 8)}
                                            </td>
                                            <td className="px-2 py-1.5">
                                                {[e.siteName, e.siteCity].filter(Boolean).join(" · ") || "—"}
                                            </td>
                                            <td className="px-2 py-1.5 font-mono">{e.woNumber ?? "—"}</td>
                                            <td className="px-2 py-1.5">
                                                {e.status === "draft" ? (
                                                    <span className="rounded-full border bg-muted px-2 py-0.5 text-[10px] font-medium">
                                                        draft → sent
                                                    </span>
                                                ) : (
                                                    <span className="rounded-full border border-primary/35 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                                                        {e.status}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-2 py-1.5 text-right tabular-nums">
                                                {formatDollars(e.subtotal)}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className="border-t bg-muted/40">
                                        <td className="px-2 py-1.5 font-semibold" colSpan={4}>
                                            {entries.length} estimate{entries.length === 1 ? "" : "s"}
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-bold tabular-nums">
                                            {formatDollars(data.totals.total)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {warning && (
                        <div
                            className="flex items-start gap-2 rounded-md border border-amber-400/70 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                            data-testid="estimate-bulk-draft-warning"
                        >
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" />
                            <span>{warning}</span>
                        </div>
                    )}

                    {data.failed.length > 0 && (
                        <div
                            className="flex items-start gap-2 rounded-md border border-amber-400/70 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                            data-testid="estimate-bulk-render-failures"
                        >
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" />
                            <span>
                                <b>
                                    {data.failed.length === 1
                                        ? "One estimate wouldn't render."
                                        : `${data.failed.length} estimates wouldn't render.`}
                                </b>{" "}
                                {data.failed.map((f) => `#${f.displayNumber ?? f.estimateId.slice(0, 8)}`).join(", ")}{" "}
                                {data.failed.length === 1 ? "isn't" : "aren't"} in the zip. Send the other{" "}
                                {entries.length}, or cancel and open {data.failed.length === 1 ? "it" : "them"} to see
                                what&apos;s wrong.
                            </span>
                        </div>
                    )}

                    {data.skipped.length > 0 && (
                        <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                            <p className="flex items-start gap-2">
                                <Info className="mt-0.5 h-3.5 w-3.5 flex-none" />
                                <span>
                                    {data.skipped.length} estimate{data.skipped.length === 1 ? "" : "s"} you picked
                                    can&apos;t be sent:
                                </span>
                            </p>
                            <ul className="mt-1 space-y-0.5 pl-5">
                                {data.skipped.map((sk) => (
                                    <li key={sk.estimateId}>
                                        <span className="font-mono">
                                            #{sk.displayNumber ?? sk.estimateId.slice(0, 8)}
                                        </span>{" "}
                                        — {sk.reason}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* ── Who it goes to ── */}
                    <div className="space-y-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Send to
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-testid="estimate-bulk-recipients">
                            {recipients.map((r) => (
                                <RecipientChip
                                    key={r.value}
                                    r={r}
                                    onRemove={() => setRecipients((prev) => prev.filter((x) => x.value !== r.value))}
                                />
                            ))}
                            {recipients.length === 0 && (
                                <span className="text-xs text-muted-foreground">
                                    Nobody yet — add an address below.
                                </span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="email"
                                className="flex-1 rounded-md border bg-background px-2 py-1 text-xs"
                                placeholder="Add an email address"
                                value={newEmail}
                                data-testid="estimate-bulk-add-email"
                                onChange={(e) => setNewEmail(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addRecipient(newEmail);
                                    }
                                }}
                            />
                            <Button size="sm" variant="outline" onClick={() => addRecipient(newEmail)}>
                                Add
                            </Button>
                        </div>
                        {addable.length > 0 && (
                            <>
                                <div className="text-[11px] text-muted-foreground">
                                    On file for this customer — click to add:
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {addable.map((r) => (
                                        <button
                                            key={r.contactMethodId ?? r.value}
                                            type="button"
                                            className="rounded-full border border-dashed px-2.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted"
                                            onClick={() =>
                                                addRecipient(r.value, {
                                                    contactMethodId: r.contactMethodId,
                                                    contactName: r.contactName,
                                                    reason: "Added",
                                                })
                                            }
                                        >
                                            {r.contactName ? `${r.contactName} · ` : ""}
                                            {r.value}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* ── The zip, spelled out before it leaves ── */}
                    <div className="space-y-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Attachment
                        </div>
                        <div className="overflow-hidden rounded-md border" data-testid="estimate-bulk-zip">
                            <div className="flex items-center justify-between gap-2 border-b bg-muted/60 px-3 py-2 text-xs font-semibold">
                                <span className="font-mono">{data.archiveName}</span>
                                <span className="font-normal text-muted-foreground">
                                    {entries.length} file{entries.length === 1 ? "" : "s"} ·{" "}
                                    {fileSize(data.totals.zipBytes)}
                                </span>
                            </div>
                            <ul>
                                {entries.map((e) => (
                                    <li
                                        key={e.estimateId}
                                        className="flex items-baseline gap-3 border-t px-3 py-1.5 text-xs first:border-t-0"
                                    >
                                        <span className="flex-1 break-words font-mono">{e.filename}</span>
                                        <span className="whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">
                                            {fileSize(e.bytes)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {!data.emailConfigured && (
                        <div className="flex items-start gap-2 rounded-md border border-amber-400/70 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" />
                            <span>
                                Outbound email isn&apos;t configured on this environment, so nothing will actually reach
                                the customer. Download the zip instead.
                            </span>
                        </div>
                    )}

                    {/* ── Footer ── */}
                    <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                        <Button size="sm" variant="outline" onClick={() => void download()}>
                            <Download className="mr-1 h-3.5 w-3.5" /> Download the zip instead
                        </Button>
                        <div className="flex items-center gap-2">
                            <Button size="sm" variant="ghost" onClick={onClose}>
                                <X className="mr-1 h-3.5 w-3.5" /> Cancel
                            </Button>
                            <Button
                                size="sm"
                                data-testid="estimate-bulk-send"
                                disabled={recipients.length === 0 || entries.length === 0 || send.isPending}
                                onClick={() => send.mutate()}
                            >
                                {send.isPending ? (
                                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Mail className="mr-1 h-3.5 w-3.5" />
                                )}
                                {sendButtonLabel(recipients.length)}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
}
